import { useCallback, useEffect, useState } from 'react'
import { useAuth } from '@/hooks/useAuth'
import { useInstitution } from '@/contexts/InstitutionContext'
import { getMe } from '@/lib/api/auth.service'
import {
  hasInstitutionDefaultDoeySellerId,
  INSTITUTION_PAYMENT_GATEWAY_UPDATED_EVENT,
} from '@/lib/institution-payment-gateway'
import type { AuthGuard } from '@/lib/jwt'

function canEvaluateGatewayAlert(
  guard: AuthGuard | null,
  institutionId: string | null,
  isCollaborator: boolean
): boolean {
  if (isCollaborator) return false
  if (guard === 'institution') return true
  if (guard === 'admin' && institutionId !== null) return true
  return false
}

/**
 * Indica se o item Gateway do menu deve exibir destaque de alerta
 * (instituição sem ID padrão do vendedor Doey salvo).
 */
export function useGatewayPaymentAlert(): boolean {
  const { guard, institution, isCollaborator, isLoading: authLoading } = useAuth()
  const { institutionId } = useInstitution()

  const shouldEvaluate = canEvaluateGatewayAlert(guard, institutionId, isCollaborator)

  const [impersonatedSellerId, setImpersonatedSellerId] = useState<string | null>(null)
  const [impersonatedLoaded, setImpersonatedLoaded] = useState(
    () => guard !== 'admin' || institutionId === null
  )

  const loadImpersonatedSellerId = useCallback(async () => {
    if (guard !== 'admin' || institutionId === null) return

    try {
      const me = await getMe()
      setImpersonatedSellerId(me.default_doey_seller_id ?? null)
    } catch {
      setImpersonatedSellerId(null)
    } finally {
      setImpersonatedLoaded(true)
    }
  }, [guard, institutionId])

  useEffect(() => {
    if (guard === 'institution') {
      setImpersonatedSellerId(null)
      setImpersonatedLoaded(true)
      return
    }

    if (guard === 'admin' && institutionId !== null) {
      setImpersonatedSellerId(null)
      setImpersonatedLoaded(false)
      void loadImpersonatedSellerId()
      return
    }

    setImpersonatedSellerId(null)
    setImpersonatedLoaded(true)
  }, [guard, institutionId, loadImpersonatedSellerId])

  useEffect(() => {
    if (!shouldEvaluate || guard !== 'admin') return

    const handleUpdate = () => {
      void loadImpersonatedSellerId()
    }

    window.addEventListener(INSTITUTION_PAYMENT_GATEWAY_UPDATED_EVENT, handleUpdate)
    return () => {
      window.removeEventListener(INSTITUTION_PAYMENT_GATEWAY_UPDATED_EVENT, handleUpdate)
    }
  }, [guard, loadImpersonatedSellerId, shouldEvaluate])

  if (!shouldEvaluate || authLoading) {
    return false
  }

  if (guard === 'admin' && !impersonatedLoaded) {
    return false
  }

  if (guard === 'institution') {
    if (!institution) return false
    return !hasInstitutionDefaultDoeySellerId(institution.default_doey_seller_id)
  }

  const sellerId = impersonatedSellerId

  return !hasInstitutionDefaultDoeySellerId(sellerId)
}
